import React, { useState, useEffect } from 'react';
import { Check, Star, FileText, Download, X, RefreshCw, Map, Home } from 'lucide-react';
import { pfotenflatrate } from '../mock';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import MembershipRegistrationForm from './MembershipRegistrationForm';
import MembershipRenewalForm from './MembershipRenewalForm';

// Tarifmodelle
const TARIFFS = {
  standard: {
    name: 'Standard-Tarif',
    description: 'Tierarzt im eigenen Wohnort',
    monthlyPrice: 10,
    additionalPetPrice: 8.50,
    paymentOptions: [
      { period: 'Quartal', price: 30, originalPrice: 30, saving: 0 },
      { period: 'Halbjährlich', price: 57, originalPrice: 60, saving: 3 },
      { period: 'Jährlich', price: 110, originalPrice: 120, saving: 10 }
    ],
    icon: Home,
    color: 'blue'
  },
  nachbarort: {
    name: 'Nachbarort-Tarif',
    description: 'Tierarzt in einem Nachbarort',
    monthlyPrice: 12,
    additionalPetPrice: 10.50,
    paymentOptions: [
      { period: 'Quartal', price: 36, originalPrice: 36, saving: 0 },
      { period: 'Halbjährlich', price: 68, originalPrice: 72, saving: 4 },
      { period: 'Jährlich', price: 132, originalPrice: 144, saving: 12 }
    ],
    icon: Map,
    color: 'orange'
  }
};

const Pfotenflatrate = () => {
  const [showRegistration, setShowRegistration] = useState(false);
  const [showRenewal, setShowRenewal] = useState(false);
  const [renewalToken, setRenewalToken] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState('standard');
  
  // Prüfe URL auf Verlängerungs-Token
  useEffect(() => {
    const checkForRenewalToken = () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.split('?')[1] || '');
      const token = params.get('renew');
      
      if (token) {
        setRenewalToken(token);
        setShowRenewal(true);
        // Clean up URL
        window.history.replaceState(null, '', window.location.pathname + '#flatrate');
      }
    };
    
    checkForRenewalToken();
    window.addEventListener('hashchange', checkForRenewalToken);
    return () => window.removeEventListener('hashchange', checkForRenewalToken);
  }, []);
  
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCloseRenewal = () => {
    setShowRenewal(false);
    setRenewalToken(null);
  };

  const currentTariff = TARIFFS[selectedTariff];

  return (
    <section id="flatrate" className="py-20 bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold">
              Spar-Angebot
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-orange-600 to-blue-600 bg-clip-text text-transparent">
              {pfotenflatrate.name}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Die clevere Mitgliedschaft für regelmäßige Fahrten – sparen Sie bis zu 450€ jährlich!
          </p>
          
          {/* Important Notice */}
          <div className="mt-6 max-w-4xl mx-auto bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <h3 className="text-lg font-bold text-blue-900 mb-2">Wichtiger Hinweis zum Start</h3>
                <p className="text-sm text-blue-800">
                  <strong>Die Pfotenflatrate startet am {pfotenflatrate.startDate}.</strong> Der Erwerb ist ab sofort möglich.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Pricing Card */}
          <Card className="relative overflow-hidden shadow-2xl border-2 border-orange-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-bl-full opacity-20"></div>
            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Star className="h-6 w-6 text-orange-500 fill-current" />
                <CardTitle className="text-3xl font-bold text-gray-900">Flatrate-Mitgliedschaft</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tarifauswahl */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg text-gray-900 text-center">Tarif wählen:</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(TARIFFS).map(([key, tariff]) => {
                    const TariffIcon = tariff.icon;
                    const isSelected = selectedTariff === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedTariff(key)}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? `border-${tariff.color}-500 bg-${tariff.color}-50 ring-2 ring-${tariff.color}-200`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{
                          borderColor: isSelected ? (tariff.color === 'blue' ? '#3b82f6' : '#f97316') : undefined,
                          backgroundColor: isSelected ? (tariff.color === 'blue' ? '#eff6ff' : '#fff7ed') : undefined
                        }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <TariffIcon className={`h-5 w-5 ${tariff.color === 'blue' ? 'text-blue-600' : 'text-orange-600'}`} />
                          <span className="font-semibold text-sm">{tariff.name}</span>
                        </div>
                        <p className="text-xs text-gray-600">{tariff.description}</p>
                        <p className={`text-lg font-bold mt-2 ${tariff.color === 'blue' ? 'text-blue-600' : 'text-orange-600'}`}>
                          ab {tariff.monthlyPrice}€/Monat
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Price Display */}
              <div className="text-center py-6 bg-gradient-to-br from-orange-100 to-blue-100 rounded-2xl">
                <div className="text-5xl font-bold text-transparent bg-gradient-to-r from-orange-600 to-blue-600 bg-clip-text">
                  {currentTariff.monthlyPrice}€
                </div>
                <div className="text-gray-600 font-medium mt-2">pro Monat / pro Tier</div>
                <div className={`text-sm mt-1 ${currentTariff.color === 'blue' ? 'text-blue-600' : 'text-orange-600'}`}>
                  {currentTariff.name}
                </div>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg text-gray-900 text-center">Zahlungsmodelle:</h3>
                <div className="grid gap-3">
                  {currentTariff.paymentOptions.map((option, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-xl border-2 ${
                        option.period === 'Jährlich' 
                          ? 'border-orange-400 bg-orange-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-semibold text-gray-900">{option.period}</span>
                          {option.saving > 0 && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              {option.saving}€ sparen
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-orange-600">{option.price}€</span>
                          {option.saving > 0 && (
                            <span className="text-sm text-gray-400 line-through ml-2">{option.originalPrice}€</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Multiple Pets Option */}
              <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-xl">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-600 text-white p-2 rounded-lg">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-blue-900">Mehrere Tiere?</h4>
                    <p className="text-sm text-blue-700">
                      Jedes weitere Tier: <span className="font-bold">+{currentTariff.additionalPetPrice.toFixed(2).replace('.', ',')}€/Monat</span>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Jedes Tier erhält eigenes Kontingent (5 Fahrten) – übertragbar zwischen Tieren!
                    </p>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg text-gray-900">Ihre Vorteile:</h3>
                {pfotenflatrate.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Savings Highlight */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-xl">
                <h4 className="font-bold text-lg mb-3">Rechenbeispiel:</h4>
                <div className="space-y-2 text-sm">
                  <p className="font-semibold">Ohne Flatrate:</p>
                  <p className="text-xs pl-2">• {pfotenflatrate.savingsExample.vetTrips}</p>
                  <p className="text-xs pl-2">• {pfotenflatrate.savingsExample.clinicTrips}</p>
                  <p className="font-semibold pt-2 border-t border-white/30">{pfotenflatrate.savingsExample.totalNormal}</p>
                  <p className="pt-2"><strong>Mit Flatrate:</strong></p>
                  <p className="text-xs pl-2">{pfotenflatrate.savingsExample.flatrateCost}</p>
                  <p className="text-3xl font-bold mt-3 pt-3 border-t border-white/30">{pfotenflatrate.savingsExample.savings}</p>
                </div>
              </div>

              <Button
                onClick={() => setShowRegistration(true)}
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
                data-testid="membership-register-btn"
              >
                🐾 Jetzt Mitglied werden
              </Button>
              
              <Button
                onClick={() => setShowRenewal(true)}
                variant="outline"
                className="w-full h-12 text-base font-medium border-2 border-green-500 text-green-600 hover:bg-green-50 mt-3"
                data-testid="membership-renew-btn"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Mitgliedschaft verlängern
              </Button>
              
              {/* Verträge Download */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Mitgliedschaftsverträge zum Download
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/public/Mitgliedschaftsvertrag_Standard.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 p-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 text-sm font-medium transition-colors"
                    data-testid="download-contract-standard"
                  >
                    <Download className="h-4 w-4" />
                    Standard-Tarif
                  </a>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/public/Mitgliedschaftsvertrag_Nachbarort.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 p-3 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg border border-orange-200 text-sm font-medium transition-colors"
                    data-testid="download-contract-nachbarort"
                  >
                    <Download className="h-4 w-4" />
                    Nachbarort-Tarif
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Details Card */}
          <div className="space-y-6">
            <Card className="shadow-lg border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <span>Vertragsbedingungen</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="benefits">
                    <AccordionTrigger className="text-left">
                      <span className="font-semibold">Enthaltene Leistungen</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 text-gray-700">
                        {pfotenflatrate.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-1" />
                            <span className="text-sm">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="conditions">
                    <AccordionTrigger className="text-left">
                      <span className="font-semibold">Vertragslaufzeit & Kündigung</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 text-gray-700">
                        {pfotenflatrate.conditions.map((condition, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span className="text-sm">{condition}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          <strong>⚠️ Wichtig:</strong> Der Leistungsbeginn liegt grundsätzlich <strong>14 Tage nach Vertragsabschluss</strong>. 
                          Diese Regelung dient dem Schutz vor Missbrauch.
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="payment">
                    <AccordionTrigger className="text-left">
                      <span className="font-semibold">Zahlungsmodalitäten</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 text-sm text-gray-700">
                        <p>
                          <strong>Monatlich:</strong> 10,00€ pro Tier
                        </p>
                        <p>
                          <strong>Zahlungsoptionen:</strong>
                        </p>
                        <ul className="ml-4 space-y-1">
                          <li>• Quartalsweise: 30,00€</li>
                          <li>• Halbjährlich: 57,00€ (3€ Ersparnis)</li>
                          <li>• Jährlich: 110,00€ (10€ Ersparnis)</li>
                        </ul>
                        <p className="text-blue-600 font-medium mt-4">
                          <strong>Zahlungsweise:</strong> Via Dauerauftrag auf unser Geschäftskonto
                        </p>
                        <div className="bg-orange-50 p-3 rounded-lg mt-4">
                          <p className="text-xs font-semibold text-orange-800 mb-2">Zusatzkosten:</p>
                          <ul className="text-xs text-orange-700 space-y-1">
                            <li>• Standard-Anfahrtskosten bei allen Transporten</li>
                            <li>• + 5€ Zusatzgebühr bei Tierklinik-Transporten</li>
                            <li>• + 20€ Notfall-Pauschale außerhalb der Geschäftszeiten</li>
                          </ul>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="booking">
                    <AccordionTrigger className="text-left">
                      <span className="font-semibold">Buchung & Terminvereinbarung</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p>
                          Termine sollten <strong>idealerweise 7 Tage im Voraus</strong> angemeldet werden, um den Transport optimal planen zu können.
                        </p>
                        <p className="text-orange-600">
                          Eine kürzere Frist ist möglich, jedoch besteht dann die Möglichkeit, dass der gewünschte Termin gar nicht oder nur mit Wartezeit (Verspätung) verfügbar ist.
                        </p>
                        <p>
                          Gilt nur für Fahrten, die keine akuten Notfälle darstellen.
                        </p>
                        <p className="text-orange-600 font-medium mt-3">
                          Für Notfälle nutzen Sie bitte unseren 24/7 Notdienst.
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="liability">
                    <AccordionTrigger className="text-left">
                      <span className="font-semibold">Haftung & Pflichten</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 text-sm text-gray-700">
                        <div>
                          <strong>Ihre Pflichten als Kunde:</strong>
                          <ul className="mt-2 space-y-1 ml-4">
                            <li>• Tier zum vereinbarten Termin bereitstellen</li>
                            <li>• Gesundheitszustand & Besonderheiten mitteilen</li>
                            <li>• Transportfähigkeit des Tieres sicherstellen</li>
                          </ul>
                        </div>
                        <div>
                          <strong>Unsere Garantie:</strong>
                          <p className="mt-2">
                            Wir sorgen für die Sicherheit Ihres Haustieres während des Transports und erbringen alle Leistungen sorgfältig, zuverlässig und termingerecht.
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>

            {/* Download CTA */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <Download className="h-8 w-8 text-blue-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">
                    Mitgliedschaftsverträge zum Download
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Laden Sie sich den Mitgliedschaftsvertrag für Ihren Tarif herunter und lesen Sie alle Bedingungen in Ruhe durch.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/public/Mitgliedschaftsvertrag_Standard.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-100"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Standard-Tarif
                      </Button>
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/public/Mitgliedschaftsvertrag_Nachbarort.html`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="outline"
                        className="border-orange-600 text-orange-600 hover:bg-orange-100"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Nachbarort-Tarif
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200">
          <h3 className="text-2xl font-bold text-center mb-8">
            Häufig gestellte Fragen
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Für wen lohnt sich die Pfotenflatrate?
              </h4>
              <p className="text-sm text-gray-600">
                Für jeden Tierbesitzer! Schon ab der <strong>ersten Fahrt zur Tierklinik</strong> sparen Sie bares Geld. Beispiel: Eine Fahrt von Hagen zur Tierklinik Haan kostet regulär 160€ – mit der Jahres-Flatrate (110€ + Anfahrt + Zuschlag) nur ca. 117€. Ideal für alle, deren Tiere regelmäßig medizinische Versorgung benötigen.
                <span className="block mt-2 text-xs text-gray-500 italic">* Diese Berechnung gilt für Fahrten während der regulären Geschäftszeiten (07:00–19:00 Uhr). Außerhalb dieser Zeiten kommt die Notfall-Pauschale von 20€ hinzu.</span>
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Was ist der Unterschied zwischen Standard- und Nachbarort-Tarif?
              </h4>
              <p className="text-sm text-gray-600">
                <strong>Standard-Tarif (ab 10€/Monat):</strong> Für Kunden, deren Haustierarzt im eigenen Wohnort ansässig ist.<br/>
                <strong>Nachbarort-Tarif (ab 12€/Monat):</strong> Für Kunden, deren Haustierarzt in einem direkten Nachbarort ansässig ist. Dieser Tarif beinhaltet die erweiterte Strecke zu Tierärzten außerhalb Ihres Wohnorts.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Kann ich die Flatrate für mehrere Tiere nutzen?
              </h4>
              <p className="text-sm text-gray-600">
                Ja! Beim Standard-Tarif kostet jedes weitere Tier 8,50€/Monat, beim Nachbarort-Tarif 10,50€/Monat. Jedes Tier erhält sein eigenes Kontingent (2 Tiere = 10 Fahrten). Fahrten sind flexibel zwischen den Tieren übertragbar!
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Welche Zusatzkosten fallen an?
              </h4>
              <p className="text-sm text-gray-600">
                Standard-Anfahrtskosten (1,50€ innerhalb Hagen, 3,50€ außerhalb) fallen bei allen Transporten an. Bei Tierklinik-Fahrten kommen zusätzlich 5€ hinzu. Für Transporte außerhalb der Geschäftszeiten (19:00–07:00 Uhr) wird eine Notfall-Pauschale von 20€ berechnet.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Wie funktioniert die Kündigung?
              </h4>
              <p className="text-sm text-gray-600">
                <strong>Mit automatischer Verlängerung:</strong> Die Kündigung ist bis spätestens 4 Wochen vor Vertragsende möglich. Ohne Kündigung verlängert sich der Vertrag automatisch um ein weiteres Jahr.<br/>
                <strong>Ohne automatische Verlängerung:</strong> Der Vertrag endet automatisch nach 1 Jahr – keine Kündigung erforderlich.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Wann beginnt die Leistung?
              </h4>
              <p className="text-sm text-gray-600">
                Der Leistungsbeginn liegt <strong>14 Tage nach Vertragsabschluss</strong>. Diese Wartezeit dient dem Schutz vor Missbrauch der Flatrate.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Ab wann kann ich die Flatrate erwerben?
              </h4>
              <p className="text-sm text-gray-600">
                Ab sofort mit Leistungsbeginn 14 Tage nach Ihrer Anmeldung.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Was passiert, wenn mein Wunschtermin belegt ist?
              </h4>
              <p className="text-sm text-gray-600">
                Kein Problem! Ich biete einen <strong>kostenlosen Terminplanungsservice</strong> an. Wenn Sie Ihren Tierarzt kurz telefonisch informieren, dass ich zur Terminabstimmung berechtigt bin, übernehme ich gerne die Koordination eines passenden Termins für Sie.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Gibt es Urlaubszeiten ohne Service?
              </h4>
              <p className="text-sm text-gray-600">
                Ja, es gibt bis zu <strong>20 Urlaubstage pro Jahr</strong>, an denen kein Service stattfindet. Die betreffenden Zeiträume werden mindestens 14 Tage im Voraus per E-Mail mitgeteilt, damit Sie rechtzeitig planen können.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      {showRegistration && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8">
            <button
              onClick={() => setShowRegistration(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="p-6 pt-12">
              <h2 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-orange-600 to-blue-600 bg-clip-text text-transparent">
                Pfotenflatrate Anmeldung
              </h2>
              <MembershipRegistrationForm onClose={() => setShowRegistration(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Renewal Modal */}
      {showRenewal && (
        <MembershipRenewalForm 
          onClose={handleCloseRenewal} 
          renewalToken={renewalToken}
        />
      )}
    </section>
  );
};

export default Pfotenflatrate;
